import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip
} from '@mui/material';
import { api } from '../utils/api';

const formatEuro = (value) => `€ ${(parseFloat(value) || 0).toFixed(2)}`;
const formatDateTime = (value) => (value ? new Date(value).toLocaleString('it-IT') : '-');

const CashRegisterHistoryDialog = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);

  const loadHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/cash-register/history?limit=50');
      setSessions(res.data?.sessions || []);
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore nel caricamento storico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelected(null);
      loadHistory();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Storico chiusure cassa</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!selected ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Chiusura</TableCell>
                    <TableCell>Apertura</TableCell>
                    <TableCell align="right">Saldo lasciato</TableCell>
                    <TableCell>Operatore</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>{formatDateTime(session.closedAt)}</TableCell>
                      <TableCell>{formatDateTime(session.openedAt)}</TableCell>
                      <TableCell align="right">{formatEuro(session.closingBalance)}</TableCell>
                      <TableCell>{session.closedBy?.name || session.closedBy?.email || '-'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setSelected(session)}>Dettagli</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!sessions.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">Nessuna chiusura registrata</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            ) : (
              <Box>
                <Button size="small" onClick={() => setSelected(null)} sx={{ mb: 2 }}>
                  ← Torna all&apos;elenco
                </Button>
                <Typography variant="subtitle2" gutterBottom>
                  Chiusura: {formatDateTime(selected.closedAt)} — {selected.closedBy?.name || '-'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Apertura: {formatDateTime(selected.openedAt)} — {selected.openedBy?.name || '-'}
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, mb: 2 }}>
                  <Typography variant="body2">Apertura: <strong>{formatEuro(selected.openingAmount)}</strong></Typography>
                  <Typography variant="body2">Entrate contanti: <strong>{formatEuro(selected.cashInTotal)}</strong></Typography>
                  <Typography variant="body2">Uscite contanti: <strong>{formatEuro(selected.cashOutTotal)}</strong></Typography>
                  <Typography variant="body2">Prelievi in giornata: <strong>{formatEuro(selected.transferPrelievoTotal)}</strong></Typography>
                  <Typography variant="body2">Versamenti in giornata: <strong>{formatEuro(selected.transferVersamentoTotal)}</strong></Typography>
                  <Typography variant="body2">Prelievo chiusura: <strong>{formatEuro(selected.closingWithdrawnAmount)}</strong></Typography>
                  <Typography variant="body2">Totale prelievi: <strong>{formatEuro(selected.totalPrelievo)}</strong></Typography>
                  <Typography variant="body2">Saldo calcolato: <strong>{formatEuro(selected.expectedAmount)}</strong></Typography>
                  <Typography variant="body2">Conteggio fisico: <strong>{formatEuro(selected.closingCountedAmount)}</strong></Typography>
                  <Typography variant="body2">Saldo lasciato in cassa: <strong>{formatEuro(selected.closingBalance)}</strong></Typography>
                </Box>
                {selected.transfers?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Prelievi e versamenti</Typography>
                    {selected.transfers.map((transfer) => (
                      <Box key={transfer.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                        <Chip
                          size="small"
                          label={transfer.type === 'PRELIEVO' ? 'Prelievo' : 'Versamento'}
                          color={transfer.type === 'PRELIEVO' ? 'warning' : 'info'}
                        />
                        <Typography variant="body2">{formatEuro(transfer.amount)} — {formatDateTime(transfer.createdAt)}</Typography>
                        {transfer.note && (
                          <Typography variant="caption" color="text.secondary">({transfer.note})</Typography>
                        )}
                      </Box>
                    ))}
                  </>
                )}
                {selected.openingDiscrepancyNote && (
                  <Alert severity="warning" sx={{ mt: 2 }}>Nota apertura: {selected.openingDiscrepancyNote}</Alert>
                )}
                {selected.closingDiscrepancyNote && (
                  <Alert severity="warning" sx={{ mt: 1 }}>Nota chiusura: {selected.closingDiscrepancyNote}</Alert>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashRegisterHistoryDialog;
